import type { VoiceSessionSnapshot } from '../client';
import type {
  NormalizedVoiceEvent,
  VoiceClientEnvelope,
  VoiceServerMessage,
} from './voice-types';

export interface VoiceSocketOptions {
  path: string;
  expectedSessionId?: string;
  expectedLeaseVersion: number;
  onSnapshot: (snapshot: VoiceSessionSnapshot) => void;
  onEvent: (event: NormalizedVoiceEvent) => void;
  onError: (error: Error) => void;
  onConnectionChange: (
    state: 'connecting' | 'connected' | 'reconnecting' | 'closed',
  ) => void;
  onRecoveryRequired?: (reason: VoiceSocketRecoveryReason) => void;
  maxReconnectAttempts?: number;
  WebSocketImpl?: typeof WebSocket;
  schedule?: (callback: () => void, delayMs: number) => number;
  cancelSchedule?: (handle: number) => void;
  random?: () => number;
  randomUUID?: () => string;
}

export interface VoiceSocketRecoveryReason {
  code:
    | 'voice_socket_reconnect_exhausted'
    | 'voice_runtime_owned_elsewhere'
    | 'voice_runtime_ownership_lost'
    | 'voice_runtime_expired_converged';
}

export class VoiceSocketError extends Error {
  constructor(
    readonly code: string,
    readonly retryable: boolean,
    message: string,
  ) {
    super(message);
    this.name = 'VoiceSocketError';
  }
}

export interface VoiceSocketLike {
  connect(): void;
  sendAudio(input: {
    dataBase64: string;
    mimeType: string;
    sampleRateHz: number;
  }): void;
  commitInput(turnKey: string): void;
  interrupt(turnKey?: string): void;
  end(): void;
}

const objectRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const finiteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const oneOf = <Value extends string>(
  value: unknown,
  allowed: readonly Value[],
): value is Value =>
  typeof value === 'string' && allowed.includes(value as Value);

const connectionStates = [
  'connecting',
  'connected',
  'reconnecting',
  'disconnected',
  'failed',
  'closed',
] as const;
const modelStates = [
  'listening',
  'recording',
  'transcribing',
  'thinking',
  'awaiting_tool_result',
  'awaiting_approval',
  'synthesizing',
  'speaking',
  'paused',
] as const;
const outputDeltaClassifications = [
  'provisional',
  'ephemeral_acknowledgement',
  'substantive',
] as const;
const outputFinalClassifications = [
  'ephemeral_acknowledgement',
  'substantive',
] as const;
const interruptionReasons = [
  'user_barge_in',
  'client_interrupt',
  'provider_interruption',
] as const;

const validCapabilities = (value: unknown): boolean => {
  const item = objectRecord(value);
  return (
    !!item &&
    [
      'inputTranscription',
      'outputTranscription',
      'bargeIn',
      'sessionContinuation',
      'toolPreamble',
    ].every((key) => typeof item[key] === 'boolean')
  );
};

const validAudioFormat = (value: unknown): boolean => {
  const item = objectRecord(value);
  return (
    !!item &&
    typeof item.mimeType === 'string' &&
    Number.isSafeInteger(item.sampleRateHz) &&
    Number(item.sampleRateHz) > 0
  );
};

const validProfileSnapshot = (value: unknown): boolean => {
  const resolved = objectRecord(value);
  const config = objectRecord(resolved?.config);
  const profile = objectRecord(resolved?.profile);
  return (
    !!config &&
    typeof config.profile === 'string' &&
    !!profile &&
    typeof profile.id === 'string' &&
    typeof profile.displayName === 'string' &&
    oneOf(profile.providerType, ['google', 'openai'] as const) &&
    oneOf(profile.mode, ['realtime', 'push_to_talk'] as const) &&
    oneOf(profile.transport, [
      'webrtc',
      'websocket_pcm',
      'request_stream',
    ] as const) &&
    typeof profile.defaultVoice === 'string' &&
    Array.isArray(profile.voices) &&
    profile.voices.every((voice) => typeof voice === 'string') &&
    validAudioFormat(profile.input) &&
    validAudioFormat(profile.output) &&
    validCapabilities(profile.capabilities)
  );
};

const validTransport = (value: unknown): boolean => {
  const item = objectRecord(value);
  return (
    !!item &&
    oneOf(item.type, ['webrtc', 'websocket_pcm', 'request_stream'] as const) &&
    typeof item.websocketPath === 'string'
  );
};

const tokenUsageFields = [
  'promptTokens',
  'responseTokens',
  'cachedPromptTokens',
  'imageOutputTokens',
  'audioInputTokens',
  'audioOutputTokens',
  'thinkingTokens',
  'toolUsePromptTokens',
] as const;
const durationUsageFields = [
  'audioInputSeconds',
  'audioOutputSeconds',
  'videoSeconds',
] as const;
const usageComponents = ['realtime', 'transcription', 'synthesis'] as const;
const usageResults = ['success', 'error'] as const;
const failureCategories = [
  'authentication',
  'authorization',
  'configuration',
  'network',
  'provider_timeout',
  'provider_rate_limit',
  'provider_error',
  'budget_exhausted',
  'invalid_request',
  'internal',
] as const;
const costSources = [
  'provider_response',
  'live_catalog',
  'configured',
] as const;
const costKinds = [
  'input',
  'cached_input',
  'output',
  'image_output',
  'audio_input',
  'audio_output',
  'video_output',
  'generation',
  'thinking',
  'tool',
  'other',
] as const;
const pricingFields = [
  'inputPerMillion',
  'outputPerMillion',
  'audioInputPerMillionTokens',
  'audioOutputPerMillionTokens',
  'audioInputPerSecond',
  'audioOutputPerSecond',
  'imageOutputPerMillion',
  'audioOutputPerMillion',
  'perGenerationUsd',
  'videoPerSecond',
] as const;

const nonNegative = (value: unknown): boolean =>
  finiteNumber(value) && value >= 0;

const validCost = (value: unknown): boolean => {
  const cost = objectRecord(value);
  if (
    !cost ||
    !nonNegative(cost.totalUsd) ||
    cost.currency !== 'USD' ||
    !oneOf(cost.source, costSources) ||
    (cost.details !== undefined && objectRecord(cost.details) === null)
  ) {
    return false;
  }
  if (cost.catalog !== undefined) {
    const catalog = objectRecord(cost.catalog);
    if (
      !catalog ||
      typeof catalog.fetchedAt !== 'string' ||
      (catalog.accountContext !== undefined &&
        typeof catalog.accountContext !== 'string') ||
      (catalog.sourceUrl !== undefined && typeof catalog.sourceUrl !== 'string')
    ) {
      return false;
    }
  }
  return (
    cost.lineItems === undefined ||
    (Array.isArray(cost.lineItems) &&
      cost.lineItems.every((value) => {
        const item = objectRecord(value);
        return (
          !!item &&
          oneOf(item.kind, costKinds) &&
          nonNegative(item.units) &&
          typeof item.unit === 'string' &&
          nonNegative(item.costUsd) &&
          (item.sku === undefined || typeof item.sku === 'string') &&
          (item.priceDimension === undefined ||
            typeof item.priceDimension === 'string')
        );
      }))
  );
};

const validPricing = (value: unknown): boolean => {
  const pricing = objectRecord(value);
  return (
    !!pricing &&
    nonNegative(pricing.inputPerMillion) &&
    nonNegative(pricing.outputPerMillion) &&
    pricingFields.every(
      (key) => pricing[key] === undefined || nonNegative(pricing[key]),
    ) &&
    (pricing.notes === undefined || typeof pricing.notes === 'string')
  );
};

const validVoiceUsageSummary = (value: unknown): boolean => {
  const summary = objectRecord(value);
  const usage = objectRecord(summary?.usage);
  if (
    !summary ||
    !usage ||
    typeof summary.requestId !== 'string' ||
    summary.requestId.length === 0 ||
    !oneOf(summary.component, usageComponents) ||
    typeof summary.model !== 'string' ||
    summary.model.length === 0 ||
    typeof summary.modelProvider !== 'string' ||
    summary.modelProvider.length === 0 ||
    !oneOf(summary.result, usageResults) ||
    (summary.providerResponseId !== undefined &&
      (typeof summary.providerResponseId !== 'string' ||
        summary.providerResponseId.length === 0)) ||
    (summary.providerId !== undefined &&
      summary.providerId !== null &&
      !Number.isSafeInteger(summary.providerId)) ||
    (summary.failureCategory !== undefined &&
      !oneOf(summary.failureCategory, failureCategories)) ||
    (summary.result === 'success' && summary.failureCategory !== undefined) ||
    (summary.pricing !== undefined && !validPricing(summary.pricing))
  ) {
    return false;
  }
  if (
    tokenUsageFields.some(
      (key) =>
        usage[key] !== undefined &&
        (!Number.isSafeInteger(usage[key]) || Number(usage[key]) < 0),
    ) ||
    durationUsageFields.some(
      (key) =>
        usage[key] !== undefined &&
        (!finiteNumber(usage[key]) || Number(usage[key]) < 0),
    )
  ) {
    return false;
  }
  return (
    (usage.modelVersion === undefined ||
      typeof usage.modelVersion === 'string') &&
    (usage.providerDetails === undefined ||
      objectRecord(usage.providerDetails) !== null) &&
    (usage.cost === undefined || validCost(usage.cost))
  );
};

const voiceEvent = (value: unknown): value is NormalizedVoiceEvent => {
  const event = objectRecord(value);
  if (
    !event ||
    typeof event.type !== 'string' ||
    typeof event.eventId !== 'string' ||
    !Number.isSafeInteger(event.sequence) ||
    !finiteNumber(event.occurredAtMs)
  ) {
    return false;
  }
  const turnKey = () => typeof event.turnKey === 'string';
  switch (event.type) {
    case 'connection_changed':
      return (
        oneOf(event.state, connectionStates) &&
        (event.recoverable === undefined ||
          typeof event.recoverable === 'boolean')
      );
    case 'speech_started':
    case 'speech_ended':
      return turnKey();
    case 'turn_completed':
      return (
        turnKey() && oneOf(event.responseKind, ['direct', 'delegated'] as const)
      );
    case 'input_transcript_delta':
      return turnKey() && typeof event.delta === 'string';
    case 'output_transcript_delta':
      return (
        turnKey() &&
        typeof event.delta === 'string' &&
        oneOf(event.classification, outputDeltaClassifications)
      );
    case 'input_transcript_final':
      return turnKey() && typeof event.text === 'string';
    case 'output_transcript_final':
      return (
        turnKey() &&
        typeof event.text === 'string' &&
        oneOf(event.classification, outputFinalClassifications)
      );
    case 'audio_output': {
      const chunk = objectRecord(event.chunk);
      return (
        turnKey() &&
        !!chunk &&
        typeof chunk.chunkId === 'string' &&
        Number.isSafeInteger(chunk.sequence) &&
        typeof chunk.dataBase64 === 'string' &&
        typeof chunk.mimeType === 'string' &&
        Number.isSafeInteger(chunk.sampleRateHz) &&
        Number(chunk.sampleRateHz) > 0
      );
    }
    case 'state_changed':
      return (
        oneOf(event.state, modelStates) &&
        (event.turnKey === undefined || typeof event.turnKey === 'string')
      );
    case 'tool_call': {
      const args = objectRecord(event.arguments);
      return (
        turnKey() &&
        typeof event.callId === 'string' &&
        event.name === 'run_agent_turn' &&
        !!args &&
        typeof args.sourceUtteranceId === 'string' &&
        typeof args.request === 'string' &&
        (args.contextNotes === undefined ||
          typeof args.contextNotes === 'string')
      );
    }
    case 'interrupted':
      return (
        oneOf(event.reason, interruptionReasons) &&
        (event.turnKey === undefined || typeof event.turnKey === 'string')
      );
    case 'error':
      return (
        typeof event.code === 'string' &&
        typeof event.message === 'string' &&
        typeof event.recoverable === 'boolean'
      );
    case 'usage_reported':
      return (
        (event.turnKey === undefined || typeof event.turnKey === 'string') &&
        validVoiceUsageSummary(event.summary)
      );
    default:
      return false;
  }
};

export const parseVoiceServerMessage = (
  value: unknown,
): VoiceServerMessage | null => {
  const message = objectRecord(value);
  if (!message || typeof message.type !== 'string') return null;
  if (message.type === 'voice_event' && voiceEvent(message.event)) {
    return { type: 'voice_event', event: message.event };
  }
  if (message.type === 'session_snapshot') {
    const session = objectRecord(message.session);
    if (
      session &&
      typeof session.sessionId === 'string' &&
      Number.isSafeInteger(session.teamId) &&
      Number.isSafeInteger(session.conversationId) &&
      Number.isSafeInteger(session.userId) &&
      Number.isSafeInteger(session.currentAgentId) &&
      typeof session.currentAgentName === 'string' &&
      validProfileSnapshot(session.profileSnapshot) &&
      oneOf(session.state, [
        'connecting',
        'active',
        'paused',
        'reconnecting',
        'ended',
      ] as const) &&
      typeof session.leaseActive === 'boolean' &&
      Number.isSafeInteger(session.leaseVersion) &&
      validTransport(session.transport) &&
      validCapabilities(session.capabilities) &&
      (session.warmResumeDeadline === null ||
        typeof session.warmResumeDeadline === 'string') &&
      (session.terminalReason === null ||
        typeof session.terminalReason === 'string')
    ) {
      return {
        type: 'session_snapshot',
        session: session as unknown as VoiceSessionSnapshot,
      };
    }
  }
  if (message.type === 'voice_error') {
    const error = objectRecord(message.error);
    if (
      error &&
      typeof error.code === 'string' &&
      typeof error.message === 'string' &&
      typeof error.retryable === 'boolean'
    ) {
      return {
        type: 'voice_error',
        error: error as unknown as {
          code: string;
          message: string;
          retryable: boolean;
        },
      };
    }
  }
  return null;
};

export class VoiceSocket implements VoiceSocketLike {
  private socket: WebSocket | null = null;
  private reconnectHandle: number | null = null;
  private reconnectAttempt = 0;
  private ended = false;
  private leaseAccepted = false;
  private lastEventSequence = -1;
  private nextChunkSequence = 0;
  private recoveryRequested = false;
  private highestAcceptedLeaseVersion: number;

  constructor(private readonly options: VoiceSocketOptions) {
    this.highestAcceptedLeaseVersion = options.expectedLeaseVersion;
  }

  connect(): void {
    if (this.ended) return;
    this.options.onConnectionChange(
      this.reconnectAttempt === 0 ? 'connecting' : 'reconnecting',
    );
    const Socket = this.options.WebSocketImpl ?? WebSocket;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = this.options.path.startsWith('ws')
      ? this.options.path
      : `${protocol}//${window.location.host}${this.options.path}`;
    const socket = new Socket(url);
    this.socket = socket;
    this.leaseAccepted = false;
    socket.addEventListener('open', () => {
      if (this.socket !== socket || this.ended) return;
    });
    socket.addEventListener('message', (message: MessageEvent) => {
      if (this.socket !== socket || typeof message.data !== 'string') return;
      let decoded: unknown;
      try {
        decoded = JSON.parse(message.data);
      } catch (_error: unknown) {
        this.options.onError(new Error('Voice socket sent invalid JSON'));
        return;
      }
      const parsed = parseVoiceServerMessage(decoded);
      if (!parsed) {
        this.options.onError(new Error('Voice socket sent an invalid message'));
        return;
      }
      this.receive(parsed);
    });
    socket.addEventListener('close', () => {
      if (this.socket !== socket) return;
      this.socket = null;
      if (!this.ended) this.scheduleReconnect();
    });
    socket.addEventListener('error', () => {
      if (this.socket === socket) {
        this.options.onError(new Error('Voice socket connection failed'));
      }
    });
  }

  sendAudio(input: {
    dataBase64: string;
    mimeType: string;
    sampleRateHz: number;
  }): void {
    if (!this.leaseAccepted) return;
    const sent = this.send({
      type: 'audio_chunk',
      chunkId: (this.options.randomUUID ?? (() => crypto.randomUUID()))(),
      sequence: this.nextChunkSequence,
      ...input,
    });
    if (sent) this.nextChunkSequence += 1;
  }

  commitInput(turnKey: string): void {
    this.send({ type: 'commit_input', turnKey });
  }

  interrupt(turnKey?: string): void {
    const sent = this.send({
      type: 'interrupt',
      ...(turnKey ? { turnKey } : {}),
    });
    if (sent) this.nextChunkSequence = 0;
  }

  end(): void {
    this.ended = true;
    if (this.reconnectHandle !== null) {
      (this.options.cancelSchedule ?? window.clearTimeout)(
        this.reconnectHandle,
      );
      this.reconnectHandle = null;
    }
    const socket = this.socket;
    this.socket = null;
    socket?.close();
    this.options.onConnectionChange('closed');
  }

  private receive(message: VoiceServerMessage): void {
    if (message.type === 'session_snapshot') {
      if (
        (this.options.expectedSessionId !== undefined &&
          message.session.sessionId !== this.options.expectedSessionId) ||
        message.session.leaseVersion < this.highestAcceptedLeaseVersion
      ) {
        return;
      }
      this.highestAcceptedLeaseVersion = message.session.leaseVersion;
      this.leaseAccepted = true;
      this.reconnectAttempt = 0;
      this.options.onConnectionChange('connected');
      this.options.onSnapshot(message.session);
      return;
    }
    if (message.type === 'voice_error') {
      this.options.onError(
        new VoiceSocketError(
          message.error.code,
          message.error.retryable,
          message.error.retryable
            ? 'Voice session failed; retry'
            : 'Voice session ended',
        ),
      );
      if (
        this.options.onRecoveryRequired &&
        [
          'voice_runtime_owned_elsewhere',
          'voice_runtime_ownership_lost',
          'voice_runtime_expired_converged',
        ].includes(message.error.code)
      ) {
        this.requestRecovery(
          message.error.code as VoiceSocketRecoveryReason['code'],
        );
        return;
      }
      if (!message.error.retryable) this.end();
      return;
    }
    if (
      !this.leaseAccepted ||
      message.event.sequence <= this.lastEventSequence
    ) {
      return;
    }
    this.lastEventSequence = message.event.sequence;
    this.options.onEvent(message.event);
  }

  private send(message: VoiceClientEnvelope): boolean {
    const socket = this.socket;
    if (socket?.readyState !== WebSocket.OPEN) return false;
    socket.send(JSON.stringify(message));
    return true;
  }

  private scheduleReconnect(): void {
    if (this.ended || this.reconnectHandle !== null) return;
    if (
      this.options.onRecoveryRequired &&
      this.reconnectAttempt >= (this.options.maxReconnectAttempts ?? 6)
    ) {
      this.requestRecovery('voice_socket_reconnect_exhausted');
      return;
    }
    this.options.onConnectionChange('reconnecting');
    const exponent = Math.min(this.reconnectAttempt++, 5);
    const baseMs = Math.min(8_000, 250 * 2 ** exponent);
    const jitter = 0.75 + (this.options.random ?? Math.random)() * 0.5;
    const schedule = this.options.schedule ?? window.setTimeout;
    this.reconnectHandle = schedule(
      () => {
        this.reconnectHandle = null;
        this.connect();
      },
      Math.round(baseMs * jitter),
    );
  }

  private requestRecovery(code: VoiceSocketRecoveryReason['code']): void {
    if (this.recoveryRequested || !this.options.onRecoveryRequired) return;
    this.recoveryRequested = true;
    this.end();
    this.options.onRecoveryRequired({ code });
  }
}
